// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Percent, DollarSign } from 'lucide-react';
import { getAdjustedIncomeStatement } from '../api/workbenchApi';
import RefreshSection from '../components/RefreshSection';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const fmtPctFromRatio = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
};

const fmtPctOfRevenue = (numerator, denom) => {
  const n = Number(numerator || 0);
  const d = Number(denom || 0);
  if (d === 0) return '—';
  return `${((n / d) * 100).toFixed(1)}%`;
};

/**
 * Adjusted Income Statement.
 *
 * Server returns an ordered list of rows with `kind`:
 *   section  → category-detail group, default-collapsed, click to expand
 *   subtotal → always-visible bold row (Gross Profit, Operating Income, ...)
 *   ebitda   → highlighted Reported EBITDA row (coral)
 *   margin   → italic % row (margin computed server-side)
 *
 * View toggle: $ values vs % of revenue. Margin rows always render as %.
 */
const AdjustedIncomeStatement = ({ mappingSessionId, bootstrap, onOpenDrilldown }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPct, setShowPct] = useState(false);
  // Sections default-collapsed unless explicitly expanded.
  const [expanded, setExpanded] = useState({});

  const load = useCallback(() => {
    if (!mappingSessionId) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdjustedIncomeStatement(mappingSessionId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingSessionId]);

  useEffect(() => load(), [load, bootstrap]);

  const periodLabels = useMemo(
    () => (data?.periods || []).map((p) => p.label),
    [data]
  );
  const revenueAdjusted = data?.totals?.revenue_adjusted || {};

  if (loading && !data) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Adjusted Income Statement
            <RefreshSection onRefresh={load} busy={loading} label="Refresh income statement" />
          </h2>
        </div>
        <div className="qoe-wb-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <div className="qoe-wb-section-header">
          <h2>
            Adjusted Income Statement
            <RefreshSection onRefresh={load} busy={loading} label="Refresh income statement" />
          </h2>
        </div>
        <div className="qoe-wb-row-error fail" style={{ maxWidth: 'unset' }}>
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  // Cell-level format. For % mode, we divide by the ADJUSTED revenue per
  // period so subtotals stay comparable when adjustments hit revenue.
  const renderCell = (value, period) => {
    if (showPct) return fmtPctOfRevenue(value, revenueAdjusted[period]);
    return fmtUSD(value);
  };

  const numCols = 1 + periodLabels.length * 3;

  const renderSectionRow = (row) => {
    const isOpen = !!expanded[row.key];
    return (
      <React.Fragment key={`section:${row.key}`}>
        <tr className="qoe-wb-is-section-row">
          <td onClick={() => setExpanded((s) => ({ ...s, [row.key]: !isOpen }))}
              style={{ cursor: 'pointer' }}>
            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span className="qoe-wb-is-section-label">{row.label}</span>
            {row.children?.length ? (
              <span className="qoe-wb-is-section-count"> · {row.children.length}</span>
            ) : (
              <span className="qoe-wb-is-section-count" style={{ opacity: 0.4 }}> · empty</span>
            )}
          </td>
          {periodLabels.map((p) => (
            <React.Fragment key={p}>
              <td className="amount" style={{ borderLeft: '1px solid var(--border)' }}>
                {renderCell(row.subtotal_reported[p], p)}
              </td>
              <td
                className="amount"
                style={{
                  color:
                    Number(row.subtotal_adjustment[p]) > 0
                      ? '#22c55e'
                      : Number(row.subtotal_adjustment[p]) < 0
                      ? '#ef4444'
                      : undefined,
                }}
              >
                {Number(row.subtotal_adjustment[p]) === 0
                  ? '—'
                  : renderCell(row.subtotal_adjustment[p], p)}
              </td>
              <td className="amount" style={{ fontWeight: 500 }}>
                {renderCell(row.subtotal_adjusted[p], p)}
              </td>
            </React.Fragment>
          ))}
        </tr>
        {isOpen &&
          row.children.map((child) => (
            <tr
              key={`acct:${row.key}:${child.original_name}`}
              className="qoe-wb-is-account-row"
              onClick={() => onOpenDrilldown && onOpenDrilldown(child.original_name)}
              style={{ cursor: onOpenDrilldown ? 'pointer' : 'default' }}
            >
              <td>
                <div className="qoe-wb-is-account">{child.original_name}</div>
                {child.category && child.category !== row.label && (
                  <div className="qoe-wb-is-cat">{child.category}</div>
                )}
              </td>
              {periodLabels.map((p) => (
                <React.Fragment key={p}>
                  <td className="amount" style={{ borderLeft: '1px solid var(--border)' }}>
                    {renderCell(child.reported[p], p)}
                  </td>
                  <td
                    className="amount"
                    style={{
                      color:
                        Number(child.adjustment[p]) > 0
                          ? '#22c55e'
                          : Number(child.adjustment[p]) < 0
                          ? '#ef4444'
                          : undefined,
                    }}
                  >
                    {Number(child.adjustment[p]) === 0 ? '—' : renderCell(child.adjustment[p], p)}
                  </td>
                  <td className="amount" style={{ fontWeight: 500 }}>
                    {renderCell(child.adjusted[p], p)}
                  </td>
                </React.Fragment>
              ))}
            </tr>
          ))}
      </React.Fragment>
    );
  };

  const renderSubtotalRow = (row) => {
    const cls =
      row.kind === 'ebitda'
        ? 'qoe-wb-is-ebitda-row'
        : row.key === 'net_income'
        ? 'qoe-wb-is-net-row'
        : 'qoe-wb-is-subtotal-row';
    return (
      <tr key={`subtotal:${row.key}`} className={cls}>
        <td>{row.label}</td>
        {periodLabels.map((p) => (
          <React.Fragment key={p}>
            <td className="amount" style={{ borderLeft: '1px solid var(--border)' }}>
              {renderCell(row.reported[p], p)}
            </td>
            <td
              className="amount"
              style={{
                color:
                  Number(row.adjustment[p]) > 0
                    ? '#22c55e'
                    : Number(row.adjustment[p]) < 0
                    ? '#ef4444'
                    : undefined,
              }}
            >
              {Number(row.adjustment[p]) === 0 ? '—' : renderCell(row.adjustment[p], p)}
            </td>
            <td className="amount">{renderCell(row.adjusted[p], p)}</td>
          </React.Fragment>
        ))}
      </tr>
    );
  };

  const renderMarginRow = (row) => (
    <tr key={`margin:${row.key}`} className="qoe-wb-is-margin-row">
      <td>{row.label}</td>
      {periodLabels.map((p) => (
        <React.Fragment key={p}>
          <td className="amount" style={{ borderLeft: '1px solid var(--border)' }}>
            {fmtPctFromRatio(row.reported?.[p])}
          </td>
          <td className="amount">—</td>
          <td className="amount">{fmtPctFromRatio(row.adjusted?.[p])}</td>
        </React.Fragment>
      ))}
    </tr>
  );

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>
          Adjusted Income Statement
          <RefreshSection onRefresh={load} busy={loading} label="Refresh income statement" />
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className={`qoe-wb-btn ${showPct ? 'ghost' : ''}`}
            onClick={() => setShowPct(false)}
            title="Show absolute dollar values"
          >
            <DollarSign size={12} /> $
          </button>
          <button
            className={`qoe-wb-btn ${showPct ? '' : 'ghost'}`}
            onClick={() => setShowPct(true)}
            title="Show each line as % of adjusted revenue"
          >
            <Percent size={12} /> %
          </button>
          <button
            className="qoe-wb-btn ghost"
            onClick={() => setExpanded({})}
            title="Collapse all sections"
          >
            Collapse all
          </button>
        </div>
      </div>

      <div className="qoe-wb-table-wrap">
        <table className="qoe-wb-table qoe-wb-is">
          <thead>
            <tr>
              <th rowSpan={2} style={{ minWidth: 240 }}>Account</th>
              {periodLabels.map((p) => (
                <th key={p} colSpan={3} style={{ textAlign: 'center', borderLeft: '1px solid var(--border)' }}>
                  {p}
                </th>
              ))}
            </tr>
            <tr>
              {periodLabels.map((p) => (
                <React.Fragment key={p}>
                  <th className="amount" style={{ borderLeft: '1px solid var(--border)' }}>Reported</th>
                  <th className="amount">Adj.</th>
                  <th className="amount">Adjusted</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((row) => {
              if (row.kind === 'section') return renderSectionRow(row);
              if (row.kind === 'margin') return renderMarginRow(row);
              return renderSubtotalRow(row);
            })}
            {periodLabels.length === 0 && (
              <tr>
                <td colSpan={numCols} style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
                  No periods found on this engagement.
                </td>
              </tr>
            )}
            {periodLabels.length > 0 && (data.rows || []).length === 0 && (
              <tr>
                <td colSpan={numCols} style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
                  Income statement returned no rows. Try the ↻ refresh, or hard-reload the page
                  (Cmd+Shift+R) to pick up the latest UI bundle.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdjustedIncomeStatement;
